import { Button, Collapse, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleCheck } from '@tabler/icons-react';
import z from 'zod';

import { appendQueryVariables } from '#core/components/chat/backends/AtlasAiBackend/appendQueryVariables';
import type { GenerateQueryResponseDTO } from '#core/components/chat/backends/AtlasAiBackend/types';
import { ChatMessage } from '#core/components/chat/messages';
import { type ToolComponentProps, useTool } from '#core/components/chat/tool';
import { PropertyTable } from '#core/components/layout/PropertyTable';
import { useCogniteClient } from '#core/hooks/useCogniteClient';
import type { DataModelView } from '#core/lib/view';

export function useQueryDataModelTool(...views: DataModelView[]) {
  const client = useCogniteClient();
  return useTool(
    'query_data_model',
    'Use this tool to query the data model.',
    z.object({
      queries: z
        .array(
          z.object({
            dataSources: z
              .enum(views.map((it) => it.externalId))
              .array()
              .describe(
                `Specify the data sources to query. Available options are: ${views.map((it) => it.externalId).join(', ')}`,
              ),
            query: z
              .string()
              .describe(`Natural language query (prompt) formulated to capture a query to the knowledge graph.

Always specify the query operation in the query: search, list or aggregate.

Examples:
- Find work orders due last week. Indicates list query operation.
- Search for asset 31-KA-XYZ. Indicates search query operation.
- List work orders for asset with {space: "<space>", externalId: "<externalId>"}. Indicates list query operation, and filter on relation.
- Count the number of work orders last week for asset with {space: "<space>", externalId: "<externalId>"}. Indicates aggregate query operation.`),
          }),
        )
        .describe(
          "Provide one or more data sources and queries to retrieve additional context required to answer the user's request.",
        ),
    }),
    async ({ args }) => {
      const viewsByExternalId = new Map(views.map((view) => [view.externalId, view]));
      const queries = args.queries.flatMap(({ dataSources, query }) =>
        dataSources.map(async (dataSource) => {
          const view = viewsByExternalId.get(dataSource);
          const prompt = query;
          try {
            if (!view) {
              throw new Error(`View with externalId '${dataSource}' not found`);
            }
            const generatedQuery = (
              await client?.post<GenerateQueryResponseDTO>(
                `/api/v1/projects/${client.project}/ai/tools/query/generate`,
                {
                  headers: { 'cdf-version': 'alpha' },
                  data: {
                    dataModels: [
                      {
                        space: view.space,
                        version: view.version,
                        externalId: 'dm_adnoc_ipso_scale',
                        views: [view.externalId],
                      },
                    ],
                    prompt,
                    stream: false,
                  },
                },
              )
            )?.data;

            const response = (
              await client?.post(`/api/v1/projects/${client.project}/ai/tools/query/execute`, {
                headers: { 'cdf-version': 'alpha' },
                data: {
                  ...generatedQuery,
                  filter: appendQueryVariables(generatedQuery),
                },
              })
            )?.data;

            return { prompt, view, response };
          } catch (e) {
            console.error('Failed to execute query:', e);
            throw e;
          }
        }),
      );

      const results = await Promise.allSettled(queries);

      return {
        message: Object.fromEntries(
          results
            .filter((result) => result.status === 'fulfilled')
            .map((result) => [result.value.view.externalId, result.value.response?.items]),
        ),
      };
    },
    toolComponent,
  );
}

function toolComponent({ output }: ToolComponentProps<Record<string, unknown>>) {
  const [opened, { toggle }] = useDisclosure(false);
  if (!output) {
    return null;
  }
  return (
    <ChatMessage role="assistant" kind="subtle">
      <Group gap="xs" align="center">
        <IconCircleCheck size={16} /> Data model query executed successfully.
        {!!Object.keys(output)?.length && (
          <Button variant="subtle" size="xs" onClick={toggle}>
            Toggle full response
          </Button>
        )}
      </Group>
      <Collapse in={opened}>
        <ScrollArea.Autosize mah="40cqh">
          <PropertyTable data={output} />
        </ScrollArea.Autosize>
      </Collapse>
    </ChatMessage>
  );
}
